import React, { useRef, useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BrandsSection from '../components/BrandsSection';
import WhyChooseUs from '../components/WhyChooseUs';
import FloatingCallButton from '../components/FloatingCallButton';
import BookingForm from '../components/BookingForm';
import { trackCallConversion } from '../utils/tracking';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/toaster';

const Link = ({ href, to, children, className, ...props }) => {
  return (
    <RouterLink to={href || to || '#'} className={className} {...props}>
      {children}
    </RouterLink>
  );
};

const fadeInUpViewport = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0 },
};

const fadeInLeftViewport = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0 },
};

const fadeInRightViewport = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0 },
};

const staggerContainerViewport = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const faqs = [
  {
    question: 'How much does iPhone Display replacement cost in Delhi?',
    answer:
      'iPhone Display replacement costs vary by model. We provide competitive pricing with OEM parts starting at Rs. 1500 only. Contact us for precise pricing based on your iPhone model and damage extent.',
  },
  {
    question: 'Do you provide same-day iPhone fix service in Delhi?',
    answer:
      'Yes, we offer same-day doorstep iPhone fix service across Delhi, Gurgaon, Noida, and Faridabad. Most fixes are completed within 2-4 hours at your location.',
  },
  {
    question: 'What iPhone models do you fix?',
    answer:
      'We fix all iPhone models from iPhone 6 to iPhone 17 Pro Max, including all variants. Our technicians are trained on every iPhone series and model.',
  },
  {
    question: 'Is there a warranty on iPhone fix?',
    answer:
      'Yes, we provide 6 months warranty on all iPhone fixes and part replacements. This covers both parts and labor for your peace of mind.',
  },
  {
    question: 'Do you use genuine iPhone parts?',
    answer:
      'We use high-quality OEM parts. For critical components, we can source parts from Apple Authorized Service Center upon request.',
  },
  {
    question: 'How do I book an iPhone fix service?',
    answer:
      "Simply call us at 8700319769 or use our online booking form. We'll schedule a convenient time and our technician will visit your location for repair.",
  },
];

const issues = [
  {
    title: 'Broken Display',
    image: '/broken_display.webp',
  },
  {
    title: 'Battery Issues',
    image: '/battery_issue.webp',
  },
  {
    title: 'Back Glass Damage',
    image: '/broken_back_glass.webp',
  },
  {
    title: 'Camera Problems',
    image: '/camera_issue.webp',
  },
  {
    title: 'Charging Port Issues',
    image: '/charging_issue.webp',
  },
  {
    title: 'Water Damage',
    image: '/water_dam.webp',
  },
  {
    title: 'Face ID Problems',
    image: '/face_id_issue.webp',
  },
  {
    title: 'Speaker Issues',
    image: '/speaker_issue.webp',
  },
];

function CommoniPhoneProblemsSection({ brand = 'apple', model = 'iphone', location = 'delhi' }) {
  const normalize = (text) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const problemTypeFromTitle = (title) => {
    const t = title.toLowerCase();
    if (t.includes('back glass')) return 'back-glass';
    if (t.includes('battery')) return 'battery';
    if (t.includes('camera')) return 'camera';
    if (t.includes('charging')) return 'chargin-port';
    if (t.includes('display')) return 'display';
    if (t.includes('microphone')) return 'microphone';
    if (t.includes('speaker')) return 'speaker';
    if (t.includes('face id')) return 'face-id';
    if (t.includes('water')) return 'water-damage';
    return normalize(title);
  };

  const buildUrl = (title) => {
    const pt = problemTypeFromTitle(title);
    const b = normalize(brand);
    const m = normalize(model);
    const loc = normalize(location);
    return `/${b}/${m}/${pt}/${b}-${m}-${pt}-fix-in-${loc}`;
  };

  return (
    <motion.section
      className="bg-gradient-to-br from-[#FEFAF4] via-[#FDF9F3] to-[#FEFAF6] border border-[#C99E54]/25 shadow-[inset_0_1px_2px_rgba(255,255,255,0.95),0_10px_24px_rgba(191,160,110,0.10)] rounded-2xl sm:rounded-3xl px-4 sm:px-6 pt-8 sm:pt-10 pb-10 sm:pb-14 my-8 sm:my-16 flex flex-col items-center relative overflow-hidden"
      variants={staggerContainerViewport}
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: 0.2 }}
    >
      <motion.h2
        variants={fadeInUpViewport}
        className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-center mb-6 sm:mb-8 text-[#0F172A] px-2 tracking-tight"
      >
        Common iPhone Problems
      </motion.h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 w-full max-w-[1000px]">
        {issues.map((issue, idx) => (
          <motion.div
            key={issue.title}
            variants={idx % 2 === 0 ? fadeInLeftViewport : fadeInRightViewport}
          >
            <Link
              href={buildUrl(issue.title)}
              className="block text-inherit no-underline bg-[#FFFDF9] border border-[#C99E54]/20 hover:border-[#C99E54]/60 rounded-xl sm:rounded-2xl p-4 sm:p-5 text-center shadow-[0_4px_16px_rgba(191,160,110,0.1)] hover:shadow-[0_8px_24px_rgba(191,160,110,0.2)] transition-all duration-300 hover:-translate-y-1 group"
            >
              <div className="flex justify-center items-center mb-3 sm:mb-4 h-32 sm:h-40 md:h-44 overflow-hidden rounded-md bg-[#FBF6F0]">
                <img
                  src={issue.image}
                  alt={issue.title}
                  width={480}
                  height={200}
                  className="object-cover w-full h-full rounded-md shadow-sm transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <h3 className="text-base sm:text-lg font-bold mb-1 text-[#0F172A] group-hover:text-[#C98E32] transition-colors">{issue.title}</h3>
            </Link>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <motion.section
      className="bg-gradient-to-br from-[#FEFAF4] via-[#FDF9F3] to-[#FEFAF6] border border-[#C99E54]/25 shadow-[inset_0_1px_2px_rgba(255,255,255,0.95),0_10px_24px_rgba(191,160,110,0.10)] rounded-2xl sm:rounded-3xl px-4 sm:px-6 pt-8 sm:pt-10 pb-10 sm:pb-14 flex flex-col items-center my-8 sm:my-16 relative overflow-hidden"
      id="faq-section"
      variants={staggerContainerViewport}
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: 0.2 }}
    >
      <motion.div
        variants={fadeInUpViewport}
        className="border border-[#C99E54]/30 bg-[#C99E54]/10 rounded-full shadow-sm inline-flex items-center text-[#C98E32] font-bold text-xs sm:text-sm uppercase tracking-widest px-4 sm:px-5 py-2 mb-4 sm:mb-5 mt-4 select-none pointer-events-none"
      >
        <span role="img" aria-label="faq">
          ❓
        </span>
        &nbsp; FAQS
      </motion.div>
      <motion.h2
        variants={fadeInUpViewport}
        className="text-xl sm:text-2xl md:text-3xl font-extrabold text-center mb-2 tracking-tight text-[#0F172A] px-2"
      >
        Frequently Asked Questions
      </motion.h2>
      <motion.p
        variants={fadeInUpViewport}
        className="text-sm sm:text-base md:text-[1.05rem] text-[#475569] text-center mb-6 sm:mb-9 max-w-[600px] px-2"
      >
        Find quick answers to the most common questions about our iPhone fix services.
      </motion.p>
      <div className="w-full flex flex-col items-center gap-3 sm:gap-4">
        {faqs.map((faq, idx) => (
          <motion.div
            className={`bg-[#FFFDF9] w-full max-w-[800px] rounded-lg sm:rounded-xl border border-[#C99E54]/20 hover:border-[#C99E54]/40 shadow-[0_4px_14px_rgba(191,160,110,0.08)] px-4 sm:px-5 pt-4 sm:pt-5 pb-2 cursor-pointer transition-all duration-200 overflow-hidden ${openIdx === idx ? 'pb-4 sm:pb-5 border-[#C99E54]/50' : ''
              }`}
            key={`faq-${idx}`}
            variants={idx % 2 === 0 ? fadeInLeftViewport : fadeInRightViewport}
            onClick={() => setOpenIdx(openIdx === idx ? -1 : idx)}
          >
            <div className="flex justify-between items-start gap-2">
              <span className="text-sm sm:text-base font-semibold text-[#0F172A] flex-1">{faq.question}</span>
              <span className="text-base sm:text-lg text-[#C98E32] font-bold select-none flex-shrink-0">
                {openIdx === idx ? '▲' : '▼'}
              </span>
            </div>
            <div
              className="text-sm sm:text-base text-[#334155] mt-2 leading-relaxed transition-[max-height,opacity] duration-500 ease-in-out overflow-hidden"
              style={{ maxHeight: openIdx === idx ? '200px' : '0', opacity: openIdx === idx ? 1 : 0 }}
            >
              {faq.answer}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}

function HeroSlider({ onBookClick }) {
  return (
    <section
      className="relative h-[400px] sm:h-[480px] md:h-[560px] lg:h-[600px] overflow-hidden heroSection"
    >
      {/* Background image layer */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(/hero.webp)` }}
      />

      {/* Dark overlay for optimal readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/70 to-transparent z-[1]" />

      {/* Text content layer */}
      <div className="relative z-[2] max-w-6xl mx-auto px-4 sm:px-6 h-full flex items-center heroContent">
        <div className="text-white max-w-3xl heroText">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
            iPhone Fix in Delhi – Display, Battery & Back Glass Replacement at Your Doorstep
          </h1>
          <p className="mt-2 sm:mt-3 md:mt-4 text-sm sm:text-base md:text-lg text-gray-200 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            Professional iPhone fix service with same-day doorstep delivery across Delhi NCR
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-start mt-8 sm:mt-10 heroCtas">
            <Button
              size="lg"
              className="!w-[70%] sm:w-auto bg-gradient-to-r from-[#F5B93A] via-[#E8A020] to-[#D99215] text-slate-950 font-bold rounded-[10px] shadow-[0_4px_20px_rgba(217,146,21,0.35)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_6px_25px_rgba(217,146,21,0.5)] border border-[#F5B93A]/40"
              asChild
            >
              <a
                href="tel:+918700319769"
                data-dsmr-phone="true"
                onClick={(e) => trackCallConversion(e, "tel:+918700319769")}
                className="flex items-center justify-center py-3 px-6"
              >
                Call Us Now : 8700319769
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
            <Button
              size="lg"
              className="!w-[70%] sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-[10px] transition-all duration-300 hover:-translate-y-1 shadow-lg border border-slate-700"
              asChild
            >
              <a href="https://wa.me/918700319769" className="flex items-center justify-center py-3 px-6">
                Contact on WhatsApp
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderShowcase() {
  const showcaseRef = useRef(null);

  const scrollByAmount = (amount) => {
    const el = showcaseRef.current;
    if (!el) return;

    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const items = [
    { key: 'ip17', label: 'iPhone 17', img: '/mobiles/iphone/iphone17/iphone_17.webp', alt: 'iPhone 17 fix service in Delhi' },
    { key: 'ip16', label: 'iPhone 16', img: '/mobiles/iphone/iphone16/iphone_16.webp', alt: 'iPhone 16 fix service in Delhi' },
    { key: 'ip15', label: 'iPhone 15', img: '/mobiles/iphone/iphone15/iphone_15.webp', alt: 'iPhone 15 series fix service in Delhi' },
    { key: 'ip14', label: 'iPhone 14', img: '/mobiles/iphone/iphone14/iphone_14.webp', alt: 'iPhone 14 fix service in Delhi' },
    { key: 'ip13', label: 'iPhone 13', img: '/mobiles/iphone/iphone13/iphone_13.webp', alt: 'iPhone 13 fix service in Delhi' },
    { key: 'ip12', label: 'iPhone 12', img: '/mobiles/iphone/iphone12/iphone_12.webp', alt: 'iPhone 12 fix service in Delhi' },
    { key: 'ip11', label: 'iPhone 11', img: '/mobiles/iphone/iphone11/iphone_11.webp', alt: 'iPhone 11 fix service in Delhi' },
    { key: 'ipx', label: 'iPhone X', img: '/mobiles/iphone/iphonex/iphone_x.webp', alt: 'iPhone X fix service in Delhi' },
    { key: 'ip8', label: 'iPhone 8', img: '/mobiles/iphone/old_iphone.webp', alt: 'iPhone 8 fix service in Delhi' },
    { key: 'ip7', label: 'iPhone 7', img: '/mobiles/iphone/old_iphone.webp', alt: 'iPhone 7 fix service in Delhi' },
    { key: 'ip6', label: 'iPhone 6', img: '/mobiles/iphone/old_iphone.webp', big: true, alt: 'iPhone 6 fix service in Delhi' },

  ];

  return (
    <div className="mt-4 flex items-center gap-2 sm:gap-3 showcaseWrap">
      <button
        type="button"
        className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-[#FFFDF9] border border-[#C99E54]/30 hover:bg-[#F7EFE3] text-[#C98E32] transition-all shadow-sm hover:scale-105 active:scale-95 cursor-pointer"
        onClick={() => scrollByAmount(-280)}
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
      <div ref={showcaseRef} className="flex-1 min-w-0 flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar scroll-smooth py-2 iphoneShowcase">
        {items.map((item) => {
          const slug = (item.label || item.key)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
          const href = `/apple/${slug}`;

          return (
            <div key={item.key} className="flex-shrink-0 iphoneModel group">
              <Link href={href} aria-label={`${item.label} fix in Delhi`} className="flex flex-col items-center gap-1 sm:gap-2">
                <div
                  className={`${item.big ? 'w-[70px] h-[85px] sm:w-[90px] sm:h-[110px]' : 'w-[60px] h-[75px] sm:w-[80px] sm:h-[100px]'} rounded-xl bg-gradient-to-br from-[#FFFFFF] to-[#FEFAF4] border border-[#C99E54]/25 shadow-[0_4px_16px_rgba(191,160,110,0.15)] group-hover:shadow-[0_8px_24px_rgba(191,160,110,0.25)] group-hover:border-[#C99E54]/50 flex items-center justify-center transition-all duration-300 group-hover:-translate-y-1`}
                >
                  <img
                    src={item.img}
                    alt={item.alt || item.label}
                    width={75}
                    height={100}
                    className="object-contain deviceImg"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs sm:text-sm font-semibold text-[#0F172A] group-hover:text-[#C98E32] text-center transition-colors">{item.label}</span>
              </Link>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-[#FFFDF9] border border-[#C99E54]/30 hover:bg-[#F7EFE3] text-[#C98E32] transition-all shadow-sm hover:scale-105 active:scale-95 cursor-pointer"
        onClick={() => scrollByAmount(280)}
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
    </div>
  );
}

export default function IphoneFixInDelhi() {
  const handleBookClick = () => {
    const target = document.querySelector('#booking-form');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col iphonefixPage bg-gradient-to-br from-[#F9F4EE] via-[#FBF6F1] to-[#FDF9F4] text-[#0F172A] relative overflow-hidden font-['Outfit','Inter',sans-serif]">
      {/* Background ambient radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[480px] bg-[radial-gradient(ellipse_at_center,rgba(235,205,155,0.28)_0%,rgba(250,246,240,0)_70%)] pointer-events-none z-0" aria-hidden="true" />

      <Navbar />
      <div className="w-full text-center bg-gradient-to-r from-[#FAF3E8] via-[#FEFAF4] to-[#FAF3E8] text-[#9A6B1F] border-b border-[#E8C898]/40 text-xs sm:text-sm md:text-base py-2.5 px-3 font-semibold shadow-sm relative z-10">
        📱 iPhone fix in Delhi NCR | Same Day Doorstep Service | Expert Technicians | DSMR
      </div>

      <div className="relative z-10 flex flex-col">
        <HeroSlider onBookClick={handleBookClick} />

        <section className="py-8 sm:py-12 iphoneModelsSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 modelsContent">
            <div className="modelsLeft">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold mb-2 text-[#0F172A] tracking-tight">All iPhone Models We Fix</h2>
              <p className="text-sm sm:text-base text-[#475569] mb-4">
                Professional fix services for 25+ iPhone variants including all series from iPhone 6 to iPhone 17 Pro Max
              </p>
              <SliderShowcase />
            </div>
          </div>
        </section>

        <section>
          <WhyChooseUs />
        </section>

        <section className="py-2 sm:py-4 commoniPhoneProblemsSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <CommoniPhoneProblemsSection />
          </div>
        </section>

        <section id="booking-form" className="py-2 sm:py-4 bookingFormSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <BookingForm />
          </div>
        </section>

        <section className="py-2 sm:py-4 faqSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <FAQSection />
          </div>
        </section>

        <section className="py-8 sm:py-10 localSeoSection">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-6 sm:mb-8 localSeoHeader">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-[#0F172A] tracking-tight px-2">iPhone Fix Services Across Delhi NCR - Complete Coverage</h2>
              <p className="text-sm sm:text-base text-[#475569] localSeoSubtitle mt-2 px-2">
                Professional doorstep iPhone fix services in all major areas of Delhi, Gurgaon, Noida, and Faridabad
              </p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 areasGrid">
              <div className="bg-gradient-to-br from-[#FFFDF9] to-[#FDF8F0] border border-[#C99E54]/25 shadow-[0_6px_20px_rgba(191,160,110,0.10)] rounded-xl sm:rounded-2xl p-4 sm:p-5 areaCard">
                <div className="text-xl sm:text-2xl">🏙️</div>
                <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mt-1 mb-2">Our South Delhi Service Areas</h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 areaList">
                  {[
                    'Vasant Kunj',
                    'Shanti Niketan',
                    'Defence Colony',
                    'Greater Kailash (GK) I & II',
                    'Jor Bagh',
                    'Hauz Khas Enclave',
                    'Panchsheel Park',
                  ].map((t) => (
                    <span key={t} className="text-[10px] sm:text-xs bg-[#FAF3E8] text-[#8C5E1A] border border-[#C99E54]/30 rounded-full px-2.5 py-1 font-medium areaItem">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FFFDF9] to-[#FDF8F0] border border-[#C99E54]/25 shadow-[0_6px_20px_rgba(191,160,110,0.10)] rounded-xl sm:rounded-2xl p-4 sm:p-5 areaCard">
                <div className="text-xl sm:text-2xl">🏘️</div>
                <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mt-1 mb-2">Our West Delhi Service Areas</h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 areaList">
                  {[
                    'Punjabi Bagh',
                    'Rajouri Garden',
                    'Patel Nagar (East & West)',
                    'Janakpuri (A,B,C Blocks)',
                    'Vikaspuri',
                    'Paschim Vihar',
                    'Kirti Nagar',
                  ].map((t) => (
                    <span key={t} className="text-[10px] sm:text-xs bg-[#FAF3E8] text-[#8C5E1A] border border-[#C99E54]/30 rounded-full px-2.5 py-1 font-medium areaItem">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FFFDF9] to-[#FDF8F0] border border-[#C99E54]/25 shadow-[0_6px_20px_rgba(191,160,110,0.10)] rounded-xl sm:rounded-2xl p-4 sm:p-5 areaCard sm:col-span-2 md:col-span-1">
                <div className="text-xl sm:text-2xl">🌆</div>
                <h3 className="text-base sm:text-lg font-bold text-[#0F172A] mt-1 mb-2">Our NCR Service Areas</h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 areaList">
                  {[
                    'Gurgaon',
                    'Noida',
                    'Faridabad',
                    'Greater Noida',
                    'Golf Course Road',
                    'DLF City',
                    'Sushant Lok 1',
                  ].map((t) => (
                    <span key={t} className="text-[10px] sm:text-xs bg-[#FAF3E8] text-[#8C5E1A] border border-[#C99E54]/30 rounded-full px-2.5 py-1 font-medium areaItem">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-6 bg-gradient-to-br from-[#FFFDF9] via-[#FDF8F0] to-[#FEFAF4] border border-[#C99E54]/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-[0_8px_25px_rgba(191,160,110,0.12)] seoBottomCard">
              <h3 className="text-lg sm:text-xl font-bold text-[#0F172A]">Why Choose DSMR for iPhone Fix in Delhi?</h3>
              <div className="text-[#334155] mt-2 space-y-2 text-xs sm:text-sm leading-relaxed seoBottomText">
                <p>
                  DSMR is an independent repair service offering professional at-home solutions for iPhone users in Delhi. We specialize in same-day doorstep service for all models, from the iPhone 6 to the latest series. Our expert technicians use high-quality parts and provide a 6-month warranty on our work.
                </p>
                <p>
                  Whether you need iPhone Display replacement, battery change, back glass fix, camera fix, charging port fix, or any other iPhone service, we deliver professional solutions right at your doorstep across Delhi, Gurgaon, Noida, and Faridabad.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      <FloatingCallButton />
      <Toaster />
    </div>
  );
}
